import { FC, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { LanguageSwitcher } from '@components';
import { DI_TOKENS } from '@constants';
import { logout } from '@features/auth';
import { useDirection, useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import {
  BuildingsIcon,
  CirclesFourIcon,
  CurrencyDollarIcon,
  HouseIcon,
  PresentationChartIcon,
  ShieldCheckIcon,
  SignOutIcon,
  ToggleLeftIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { AuthStore } from '@stores';
import { NoFlipBox } from '@utils';

import { adminColors } from './adminTheme';

const initials = (a?: string, b?: string) =>
  `${(a ?? '').charAt(0)}${(b ?? '').charAt(0)}`.toUpperCase() || 'PA';

export const AdminLayout: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  // One source for the panel's direction. `useDirection` reports RTL when
  // either i18next or the MUI theme does, so the sidebar/content row never
  // renders unflipped in the window where those two signals disagree.
  const { dir, isRtl } = useDirection();
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const user = authStore.userProfile?.user;
  // Platform admins aren't always tenant members. Only offer a way back to the
  // normal app view when this admin actually belongs to an organization.
  const belongsToOrg = Boolean(authStore.userProfile?.tenant?.id);

  const nav = [
    {
      key: 'dashboard',
      label: t('app.admin.nav.dashboard'),
      icon: CirclesFourIcon,
      to: routes.adminDashboard(),
      match: '/admin/dashboard',
    },
    {
      key: 'organizations',
      label: t('app.admin.nav.organizations'),
      icon: BuildingsIcon,
      to: routes.adminTenants(),
      match: '/admin/organizations',
    },
    {
      key: 'users',
      label: t('app.admin.nav.users'),
      icon: UsersIcon,
      to: routes.adminUsers(),
      match: '/admin/users',
    },
    {
      key: 'demo-requests',
      label: t('app.admin.nav.demoRequests'),
      icon: PresentationChartIcon,
      to: routes.adminDemoRequests(),
      match: '/admin/demo-requests',
    },
    {
      key: 'features',
      label: t('app.admin.nav.features'),
      icon: ToggleLeftIcon,
      to: routes.adminFeatures(),
      match: '/admin/features',
    },
    // Last: analytics rather than day-to-day operations.
    {
      key: 'usage',
      label: t('app.admin.nav.usage'),
      icon: CurrencyDollarIcon,
      to: routes.adminUsage(),
      match: '/admin/usage',
    },
  ];

  const handleLogout = async () => {
    // Keep the user here with a spinner until logout settles; navigating to
    // /login before the request resolves has caused issues.
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    // NoFlipBox rather than `direction` in `sx`: the @mui/stylis-plugin-rtl
    // cache rewrites that property value (rtl→ltr) and would leave the
    // sidebar/content row unflipped. The wrapper marks it `@noflip` and also
    // sets the native `dir` attribute, which stylis never touches.
    <NoFlipBox
      mode={dir}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: c.canvas,
      }}
    >
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <Box
        sx={{
          height: 52,
          flexShrink: 0,
          backgroundColor: c.surface,
          borderBottom: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <ShieldCheckIcon size={24} color={c.teal} weight="fill" />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: c.ink, letterSpacing: '-0.01em' }}>
            Komplyance
          </Typography>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 600,
              color: c.violet,
              backgroundColor: c.violetBg,
              border: `1px solid ${c.violetBorder}`,
              borderRadius: 1.5,
              px: 0.875,
              py: 0.25,
              letterSpacing: '0.02em',
            }}
          >
            {t('app.admin.badge')}
          </Box>
        </Box>
        <Box sx={{ width: '1px', height: 18, backgroundColor: c.border }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: c.sub }}>
          {t('app.admin.panelTitle')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <LanguageSwitcher variant="pill" />
      </Box>

      {/* ── Body: sidebar + content ─────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, gap: 1.5, p: 1.5, overflow: 'hidden', minHeight: 0 }}>
        {/* Sidebar */}
        <Box sx={{ width: 262, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box
            sx={{
              backgroundColor: c.surface,
              border: `1px solid ${c.border}`,
              borderRadius: 3,
              p: 1.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: c.faint,
                letterSpacing: '0.07em',
                px: 1.25,
                pt: 0.5,
                pb: 1,
              }}
            >
              {t('app.admin.nav.platform')}
            </Typography>
            {nav.map((item) => {
              const active = location.pathname.startsWith(item.match);
              const Icon = item.icon;
              return (
                <Box
                  key={item.key}
                  onClick={() => navigate(item.to)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.25,
                    py: 1.125,
                    borderRadius: 2.25,
                    cursor: 'pointer',
                    backgroundColor: active ? theme.palette.grey[200] : 'transparent',
                    '&:hover': { backgroundColor: theme.palette.grey[200] },
                  }}
                >
                  <Icon
                    size={18}
                    weight={active ? 'fill' : 'regular'}
                    color={active ? c.primary : c.sub}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: active ? 500 : 400,
                      color: active ? c.primary : c.sub,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            })}

            <Box sx={{ flex: 1 }} />

            {/* Back to the normal org app — the mirror of the admin-panel entry
                in the app sidebar. Only shown when this admin belongs to an org. */}
            {belongsToOrg && (
              <>
                <Box sx={{ height: '1px', backgroundColor: c.border, my: 0.5 }} />
                <Box
                  onClick={() => navigate(routes.dashboard())}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.25,
                    py: 1.125,
                    borderRadius: 2.25,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.grey[200] },
                  }}
                >
                  <HouseIcon size={18} color={c.sub} />
                  <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 400, color: c.sub }}>
                    {t('app.admin.nav.backToApp')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Profile / sign-out */}
          <Box
            sx={{
              backgroundColor: c.surface,
              border: `1px solid ${c.border}`,
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexShrink: 0,
            }}
          >
            <Avatar
              src={user?.image}
              sx={{
                width: 36,
                height: 36,
                fontSize: 12.5,
                fontWeight: 600,
                background: 'linear-gradient(135deg,#623AE6,#0063B3)',
              }}
            >
              {initials(user?.firstName, user?.lastName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: c.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.firstName || user?.lastName
                  ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
                  : user?.username}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.125 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.violet }} />
                <Typography sx={{ fontSize: 11, color: c.violet, fontWeight: 500 }}>
                  {t('app.admin.role.platformAdmin')}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleLogout}
              disabled={isLoggingOut}
              title={t('app.admin.signOut')}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${c.border}`,
                color: c.sub,
                '&:hover': { backgroundColor: theme.palette.grey[200] },
              }}
            >
              {isLoggingOut ? (
                <CircularProgress size={15} sx={{ color: c.sub }} />
              ) : (
                <SignOutIcon size={15} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: c.surface,
            border: `1px solid ${c.border}`,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </NoFlipBox>
  );
});
